class ZenAccordion extends HTMLElement {
  constructor() {
    super();
    this._onTriggerClick = this._onTriggerClick.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onBreakpointChange = this._onBreakpointChange.bind(this);
  }

  connectedCallback() {
    this._items = Array.from(this.querySelectorAll('.zen-pd-accordion__item'));

    // Remember each item's original "default open" intent from the markup
    this._items.forEach((item) => {
      const isDefaultOpen =
        item.hasAttribute('data-default-open') ||
        item.classList.contains('zen-pd-accordion__item--expanded');
      item.dataset.defaultOpen = isDefaultOpen ? 'true' : 'false';

      const trigger = item.querySelector('.zen-pd-accordion__trigger');
      if (!trigger) return;
      trigger.addEventListener('click', this._onTriggerClick);
    });

    // Apply initial state based on current breakpoint
    this._desktopMQ = window.matchMedia('(min-width: 1024px)');
    requestAnimationFrame(() => this._applyBreakpointState(false));

    // Listen for breakpoint changes (no refresh needed)
    if (typeof this._desktopMQ.addEventListener === 'function') {
      this._desktopMQ.addEventListener('change', this._onBreakpointChange);
    } else if (typeof this._desktopMQ.addListener === 'function') {
      // Safari < 14
      this._desktopMQ.addListener(this._onBreakpointChange);
    }

    window.addEventListener('resize', this._onResize, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._onResize);
    if (this._desktopMQ) {
      if (typeof this._desktopMQ.removeEventListener === 'function') {
        this._desktopMQ.removeEventListener('change', this._onBreakpointChange);
      } else if (typeof this._desktopMQ.removeListener === 'function') {
        this._desktopMQ.removeListener(this._onBreakpointChange);
      }
    }
    if (this._items) {
      this._items.forEach((item) => {
        const trigger = item.querySelector('.zen-pd-accordion__trigger');
        if (trigger) trigger.removeEventListener('click', this._onTriggerClick);
      });
    }
  }

  _onBreakpointChange() {
    this._applyBreakpointState(true);
  }

  _applyBreakpointState(animate) {
    const isDesktop = this._desktopMQ.matches;
    this._items.forEach((item) => {
      const isDefaultOpen = item.dataset.defaultOpen === 'true';
      const isExpanded = item.classList.contains('zen-pd-accordion__item--expanded');

      if (isDesktop) {
        // On desktop: open default-open items that aren't already open
        if (isDefaultOpen && !isExpanded) {
          this._open(item, animate);
        } else if (isExpanded) {
          // Recalculate height for already-open items
          const content = item.querySelector('.zen-pd-accordion__content');
          if (content) content.style.maxHeight = content.scrollHeight + 'px';
        }
      } else {
        // On mobile: close everything
        if (isExpanded) {
          this._close(item);
        }
      }
    });
  }

  _onTriggerClick(event) {
    const trigger = event.currentTarget;
    const item = trigger.closest('.zen-pd-accordion__item');
    if (!item) return;

    if (item.classList.contains('zen-pd-accordion__item--expanded')) {
      this._close(item);
    } else {
      this._open(item, true);
    }
  }

  _open(item, animate) {
    const content = item.querySelector('.zen-pd-accordion__content');
    const trigger = item.querySelector('.zen-pd-accordion__trigger');
    if (!content || !trigger) return;

    item.classList.add('zen-pd-accordion__item--expanded');
    trigger.setAttribute('aria-expanded', 'true');

    const target = content.scrollHeight;
    if (animate) {
      content.style.maxHeight = target + 'px';
    } else {
      // Skip transition on initial render
      const prev = content.style.transition;
      content.style.transition = 'none';
      content.style.maxHeight = target + 'px';
      // Force reflow then restore
      void content.offsetHeight;
      content.style.transition = prev;
    }
  }

  _close(item) {
    const content = item.querySelector('.zen-pd-accordion__content');
    const trigger = item.querySelector('.zen-pd-accordion__trigger');
    if (!content || !trigger) return;

    // Set explicit height first so the transition has a starting value
    content.style.maxHeight = content.scrollHeight + 'px';
    // Force reflow
    void content.offsetHeight;

    item.classList.remove('zen-pd-accordion__item--expanded');
    trigger.setAttribute('aria-expanded', 'false');
    content.style.maxHeight = '0px';
  }

  _onResize() {
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => {
      this._items.forEach((item) => {
        if (item.classList.contains('zen-pd-accordion__item--expanded')) {
          const content = item.querySelector('.zen-pd-accordion__content');
          if (content) content.style.maxHeight = content.scrollHeight + 'px';
        }
      });
    }, 120);
  }
}

if (!customElements.get('zen-accordion')) {
  customElements.define('zen-accordion', ZenAccordion);
}

(function () {
  'use strict';

  var lastTrigger = null;
  var openModal = null;

  function openById(id, trigger) {
    var modal = document.getElementById(id);
    if (!modal) return;
    if (openModal) closeModal();
    modal.hidden = false;
    document.body.classList.add('zen-pd-modal-open');
    lastTrigger = trigger || null;
    openModal = modal;
    var closeBtn = modal.querySelector('[data-zen-pd-close]');
    if (closeBtn && closeBtn.focus) setTimeout(function () { closeBtn.focus(); }, 30);
  }

  function closeModal() {
    if (!openModal) return;
    openModal.hidden = true;
    document.body.classList.remove('zen-pd-modal-open');
    var toFocus = lastTrigger;
    openModal = null;
    lastTrigger = null;
    if (toFocus && toFocus.focus) toFocus.focus();
  }

  document.addEventListener('click', function (e) {
    var opener = e.target && e.target.closest && e.target.closest('[data-zen-pd-open]');
    if (opener) {
      e.preventDefault();
      openById(opener.getAttribute('data-zen-pd-open'), opener);
      return;
    }
    var closer = e.target && e.target.closest && e.target.closest('[data-zen-pd-close]');
    if (closer && closer.closest('[data-zen-pd-modal]')) {
      e.preventDefault();
      closeModal();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openModal) closeModal();
    if ((e.key === 'Enter' || e.key === ' ') && e.target && e.target.closest && e.target.closest('[data-zen-pd-close]')) {
      e.preventDefault();
      closeModal();
    }
  });
})();
